import { isValidCode, normalizeCode } from "@brouhaha/codes";
import { t } from "@brouhaha/i18n";
import { Button, Card } from "@brouhaha/ui";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useId, useState } from "react";
import type { FormEvent } from "react";

import {
  authClient,
  deleteMyAccount,
  downloadMyData,
  fetchProviders,
  keepMyShare,
  readBearer,
  restoreMyAccount,
} from "../lib/auth-client";
import type { Providers } from "../lib/auth-client";
import { API_URL } from "../lib/flags";
import {
  cachedSession,
  forgetSession,
  keepSession,
  localSessionStore,
  subjectOf,
} from "../lobby/session";

/**
 * Design doc 6.4, "Sign in to keep your share." One page, reached from
 * the table with the lobby code in ?c=, or from a magic-link email with
 * the token in ?token=. Passkeys and email links work anywhere; Google
 * and Apple appear only when this deployment has their keys.
 */
export const Route = createFileRoute("/account")({
  component: AccountPage,
  validateSearch: (search: Record<string, unknown>): { c?: string; token?: string } => ({
    ...(typeof search["c"] === "string" && isValidCode(normalizeCode(search["c"]))
      ? { c: normalizeCode(search["c"]) }
      : {}),
    ...(typeof search["token"] === "string" && search["token"] ? { token: search["token"] } : {}),
  }),
});

type Step =
  | { kind: "loading" }
  | { kind: "off" }
  | { kind: "choose"; providers: Providers }
  | { kind: "sent"; email: string }
  | { kind: "verifying" }
  | { kind: "kept"; email: string | null; claimed: number }
  /** Signed back in while the account waits to be deleted. */
  | { kind: "deleting"; purgeAfter: number }
  /** Just deleted: signed out, with the date it goes for good. */
  | { kind: "deleted"; purgeAfter: number }
  | { kind: "failed" };

function AccountPage() {
  const { c: code, token } = Route.useSearch();
  const [step, setStep] = useState<Step>({ kind: API_URL ? "loading" : "off" });
  const [busy, setBusy] = useState(false);

  // Signed in (by any route): swap to the account session and claim the share.
  async function keep() {
    const guest = cachedSession(localSessionStore());
    const guestToken = guest && subjectOf(guest)?.startsWith("guest:") ? guest : null;
    const kept = await keepMyShare(guestToken, code ?? null);
    if (!kept) {
      setStep({ kind: "failed" });
      return;
    }
    if (kept.kind === "deleting") {
      setStep(kept);
      return;
    }
    keepSession(localSessionStore(), kept.token);
    const session = await authClient().getSession();
    setStep({ kind: "kept", email: session.data?.user.email ?? null, claimed: kept.claimed });
  }

  useEffect(() => {
    if (!API_URL) return;
    let live = true;
    void (async () => {
      if (token) {
        setStep({ kind: "verifying" });
        const verified = await authClient().magicLink.verify({ query: { token } });
        if (!live) return;
        if (verified.error) {
          setStep({ kind: "failed" });
          return;
        }
        await keep();
        return;
      }
      // Already signed in on this phone: straight to the account.
      if (readBearer()) {
        const session = await authClient().getSession();
        if (!live) return;
        if (session.data?.user) {
          await keep();
          return;
        }
      }
      const providers = await fetchProviders();
      if (!live) return;
      setStep(providers ? { kind: "choose", providers } : { kind: "failed" });
    })();
    return () => {
      live = false;
    };
    // The token and code come from the URL and do not change while the page is open.
  }, [token]);

  async function restore() {
    setBusy(true);
    const ok = await restoreMyAccount();
    if (ok) await keep();
    else setStep({ kind: "failed" });
    setBusy(false);
  }

  async function remove() {
    setBusy(true);
    const purgeAfter = await deleteMyAccount();
    setBusy(false);
    if (purgeAfter === null) {
      setStep({ kind: "failed" });
      return;
    }
    // This phone goes back to being a guest.
    forgetSession(localSessionStore());
    setStep({ kind: "deleted", purgeAfter });
  }

  async function withPasskey() {
    setBusy(true);
    const result = await authClient().signIn.passkey();
    setBusy(false);
    if (result?.error) {
      setStep({ kind: "failed" });
      return;
    }
    await keep();
  }

  async function withSocial(provider: "google" | "apple") {
    setBusy(true);
    const back = new URL(window.location.href);
    back.search = code ? `?c=${code}` : "";
    await authClient().signIn.social({ provider, callbackURL: back.toString() });
    setBusy(false);
  }

  async function withEmail(email: string) {
    setBusy(true);
    const result = await authClient().signIn.magicLink({ email });
    setBusy(false);
    setStep(result.error ? { kind: "failed" } : { kind: "sent", email });
  }

  const backTo = code ? (
    <Link to="/c/$code" params={{ code }} search={{}} className="contents">
      <Button variant="ghost" full tabIndex={-1}>
        {t("account.backToTable")}
      </Button>
    </Link>
  ) : (
    <Link to="/" className="contents">
      <Button variant="ghost" full tabIndex={-1}>
        {t("code.back")}
      </Button>
    </Link>
  );

  return (
    <main className="flex flex-1 flex-col gap-6">
      <header>
        <h1 className="text-xl font-bold">{t("account.title")}</h1>
        <p className="mt-1 text-sm text-ink-muted">{t("account.tagline")}</p>
      </header>

      {step.kind === "off" && (
        <Card>
          <p role="alert">{t("account.off")}</p>
        </Card>
      )}
      {(step.kind === "loading" || step.kind === "verifying") && (
        <p role="status" className="text-sm text-ink-muted">
          {t(step.kind === "loading" ? "account.loading" : "account.verifying")}
        </p>
      )}
      {step.kind === "failed" && (
        <Card>
          <p role="alert" className="font-semibold">
            {t("account.failed")}
          </p>
        </Card>
      )}
      {step.kind === "sent" && (
        <Card>
          <p role="status" className="font-semibold">
            {t("account.sent", { email: step.email })}
          </p>
          <p className="mt-2 text-sm text-ink-muted">{t("account.sentHint")}</p>
        </Card>
      )}
      {step.kind === "kept" && (
        <Card>
          <p role="status" className="font-semibold" data-testid="kept">
            {step.claimed > 0 ? t("account.keptWithXp", { xp: step.claimed }) : t("account.kept")}
          </p>
          {step.email && <p className="mt-2 text-sm text-ink-muted">{step.email}</p>}
        </Card>
      )}
      {step.kind === "kept" && <YourData busy={busy} onDelete={() => void remove()} />}
      {step.kind === "deleting" && (
        <>
          <Card>
            <p role="status" className="font-semibold" data-testid="deleting">
              {t("account.deleting", { date: day(step.purgeAfter) })}
            </p>
          </Card>
          <Button full disabled={busy} onClick={() => void restore()}>
            {t("account.restore")}
          </Button>
        </>
      )}
      {step.kind === "deleted" && (
        <Card>
          <p role="status" className="font-semibold" data-testid="deleted">
            {t("account.deleted", { date: day(step.purgeAfter) })}
          </p>
        </Card>
      )}
      {step.kind === "choose" && (
        <>
          {step.providers.passkey && (
            <Button full disabled={busy} onClick={() => void withPasskey()}>
              {t("account.passkey")}
            </Button>
          )}
          {step.providers.google && (
            <Button
              variant="secondary"
              full
              disabled={busy}
              onClick={() => void withSocial("google")}
            >
              {t("account.google")}
            </Button>
          )}
          {step.providers.apple && (
            <Button
              variant="secondary"
              full
              disabled={busy}
              onClick={() => void withSocial("apple")}
            >
              {t("account.apple")}
            </Button>
          )}
          {step.providers.magicLink && <EmailForm busy={busy} onSend={withEmail} />}
        </>
      )}

      <div className="flex-1" />
      {backTo}
    </main>
  );
}

function EmailForm({ busy, onSend }: { busy: boolean; onSend: (email: string) => void }) {
  const id = useId();
  const [email, setEmail] = useState("");
  const [invalid, setInvalid] = useState(false);
  function submit(e: FormEvent) {
    e.preventDefault();
    const clean = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clean)) {
      setInvalid(true);
      return;
    }
    onSend(clean);
  }
  return (
    <form onSubmit={submit} noValidate className="flex flex-col gap-3">
      <Card>
        <label htmlFor={id} className="block font-semibold">
          {t("account.emailLabel")}
        </label>
        <p className="mt-1 text-sm text-ink-muted">
          {invalid ? t("account.emailInvalid") : t("account.emailHint")}
        </p>
        <input
          id={id}
          type="email"
          name="email"
          autoComplete="email"
          inputMode="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setInvalid(false);
          }}
          aria-invalid={invalid || undefined}
          className="mt-4 min-h-target w-full rounded-2xl border-2 border-line bg-well px-4 text-lg text-ink aria-invalid:border-rose"
        />
      </Card>
      <Button type="submit" variant="secondary" full disabled={busy}>
        {t("account.sendLink")}
      </Button>
    </form>
  );
}

const day = (ms: number) =>
  new Date(ms).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" });

/** Design doc 6.13, Data: download it all, or delete the account (immediate, 30 days to change your mind). */
function YourData({ busy, onDelete }: { busy: boolean; onDelete: () => void }) {
  const [confirming, setConfirming] = useState(false);
  const [failed, setFailed] = useState(false);
  return (
    <Card tone="sunken">
      <h2 className="font-semibold">{t("account.data.title")}</h2>
      <p className="mt-1 text-sm text-ink-muted">{t("account.data.hint")}</p>
      {failed && (
        <p role="alert" className="mt-2 text-sm text-rose">
          {t("account.data.failed")}
        </p>
      )}
      <div className="mt-4 flex flex-col gap-2">
        <Button
          variant="secondary"
          full
          disabled={busy}
          onClick={() => void downloadMyData().then((ok) => setFailed(!ok))}
        >
          {t("account.data.download")}
        </Button>
        {confirming ? (
          <>
            <p role="alert" className="mt-2">
              {t("account.delete.confirm")}
            </p>
            <Button variant="danger" full disabled={busy} onClick={onDelete}>
              {t("account.delete.yes")}
            </Button>
            <Button variant="ghost" full onClick={() => setConfirming(false)}>
              {t("account.delete.no")}
            </Button>
          </>
        ) : (
          <Button variant="ghost" full onClick={() => setConfirming(true)}>
            {t("account.delete.start")}
          </Button>
        )}
      </div>
    </Card>
  );
}
